import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
}

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const particlesRef = useRef<Particle[]>([]);
  const animFrameRef = useRef<number>(0);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const NEON = '57,255,20';
    const CONNECTION_DIST = 160;
    const MOUSE_DIST = 200;
    const NUM_PARTICLES = 110;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const initParticles = () => {
      particlesRef.current = Array.from({ length: NUM_PARTICLES }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1.5,
        opacity: Math.random() * 0.5 + 0.4,
      }));
    };
    initParticles();

    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };
    const onMouseLeave = () => {
      mouseRef.current = { x: -9999, y: -9999 };
    };

    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const particles = particlesRef.current;
      const mouse = mouseRef.current;

      // Update positions
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
      });

      // Draw connections between particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            const alpha = (1 - dist / CONNECTION_DIST) * 0.35;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${NEON},${alpha})`;
            ctx.lineWidth = 0.8;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }

        // Mouse connections
        const mdx = particles[i].x - mouse.x;
        const mdy = particles[i].y - mouse.y;
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (mdist < MOUSE_DIST) {
          const alpha = (1 - mdist / MOUSE_DIST) * 0.7;
          ctx.beginPath();
          ctx.strokeStyle = `rgba(${NEON},${alpha})`;
          ctx.lineWidth = 1.2;
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.stroke();

          // Subtle pull toward mouse
          particles[i].x += (mouse.x - particles[i].x) * 0.003;
          particles[i].y += (mouse.y - particles[i].y) * 0.003;
        }
      }

      // Draw particles
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${NEON},${p.opacity})`;
        ctx.fill();

        // Glow
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius + 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${NEON},0.08)`;
        ctx.fill();
      });

      // Mouse cursor dot
      if (mouse.x > 0 && mouse.x < canvas.width) {
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${NEON},0.6)`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 14, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${NEON},0.2)`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: '#050505' }}
    >
      {/* Interactive Particle Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full z-0"
        style={{ cursor: 'none' }}
      />

      {/* Subtle radial glow center */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 30% 50%, rgba(57,255,20,0.04) 0%, transparent 70%)',
        }}
      />

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 z-0 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, #050505)' }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-28 pb-20 pointer-events-none">
        <div className="max-w-xl pointer-events-auto">

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
            A tecnologia que{' '}
            <span className="text-neon-green">sustenta</span>{' '}
            o seu negócio.
          </h1>

          <p className="text-gray-300 text-lg md:text-xl mb-8 leading-relaxed">
            A gente estrutura, protege e mantém tudo funcionando.{' '}
            <br className="hidden md:block" />
            Do Wi-Fi ao servidor — infraestrutura de TI com padrão profissional.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => handleScroll('#contact')}
              className="inline-flex items-center justify-center gap-2 bg-neon-green text-black font-bold px-7 py-3.5 rounded-md hover:bg-green-400 transition-all duration-200 cursor-pointer whitespace-nowrap text-base"
            >
              <i className="ri-message-3-line" />
              Fale com um Especialista
            </button>
            <button
              onClick={() => handleScroll('#services')}
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-semibold px-7 py-3.5 rounded-md hover:border-neon-green hover:text-neon-green transition-all duration-200 cursor-pointer whitespace-nowrap text-base"
            >
              Nossos Serviços
              <i className="ri-arrow-right-line" />
            </button>
          </div>

          {/* Stats */}
          <div className="mt-14 flex gap-4">
            <div className="text-center border border-neon-green/20 rounded-xl px-8 py-4 bg-neon-green/5 flex-1">
              <div className="text-3xl md:text-4xl font-extrabold text-neon-green">+100</div>
              <div className="text-gray-400 text-sm mt-1 tracking-wide">Projetos Entregues</div>
            </div>
            <div className="text-center border border-neon-green/20 rounded-xl px-8 py-4 bg-neon-green/5 flex-1">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-1">
                <i className="ri-award-fill text-neon-green text-3xl" />
              </div>
              <div className="text-gray-400 text-sm tracking-wide">Especialistas<br />Certificados</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce pointer-events-none">
        <span className="text-gray-500 text-xs">Scroll</span>
        <i className="ri-arrow-down-line text-neon-green text-lg" />
      </div>
    </section>
  );
}
